import { Trans, useLingui } from "@lingui/react/macro";
import {
  ActionIcon,
  Box,
  Grid,
  Group,
  LoadingOverlay,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import dayjs from "dayjs";

import PDFSummaryModal from "@/features/bookings/booking-pdf-summary/pdf-summary-modal";
import DuplicateBooking from "@/features/bookings/duplicate-booking/duplicate-booking";
import SendBooking from "@/features/bookings/send-booking";
import AdditionalInformation from "@/features/bookings/update-booking/additional-information";
import GroupInformation from "@/features/bookings/update-booking/group-information";
import Products from "@/features/bookings/update-booking/products";
import Program from "@/features/bookings/update-booking/program";
import StatusSelector from "@/features/bookings/update-booking/status-selector";
import TodaysBookings from "@/features/bookings/update-booking/todays-bookings";
import VisitInformation from "@/features/bookings/update-booking/visit-information";
import { bookingStatusLabels } from "@/features/bookings/utils/status";
import {
  getGetBookingQueryOptions,
  useGetBooking,
  useGetpreviousandnextbookingsforalocation,
} from "@/shared/api/generated/booking";
import { Can } from "@/shared/providers/casl-provider";
import {
  GetFieldsBySubjectKind,
  getSubject,
} from "@/shared/providers/casl-provider/authorization/subjects";

export const Route = createFileRoute(
  "/_app/(single-location)/museum/$museumId/bookings/$bookingId/",
)({
  beforeLoad: async ({ params, context }) => {
    const booking = await context.queryClient.ensureQueryData(
      getGetBookingQueryOptions(parseInt(params.bookingId)),
    );

    const canReadThisBooking = context.ability.can(
      "read",
      getSubject("Booking", {
        id: booking.data.id,
        location: { id: booking.data.location.id },
      }),
    );

    if (!canReadThisBooking) {
      console.error(
        `User does not have access to Booking ${booking.data.id} in Museum ${booking.data.location.id}`,
      );
      redirect({
        to: "/unauthorized",
        throw: true,
      });
    }
  },
  component: () => <BookingDetailsPage />,
});

function BookingDetailsPage() {
  const { bookingId, museumId } = Route.useParams();

  const {
    data: booking,
    isLoading,
    isError,
  } = useGetBooking(parseInt(bookingId));

  const { data: browseBookings, isLoading: isLoadingBrowseBookings } =
    useGetpreviousandnextbookingsforalocation(museumId, parseInt(bookingId));

  const shouldDisableNext =
    isLoadingBrowseBookings || browseBookings?.next === null;

  const shouldDisablePrevious =
    isLoadingBrowseBookings || browseBookings?.previous === null;

  const { i18n } = useLingui();

  return (
    <Stack p={16} mih="50%" key={bookingId}>
      <Group>
        <Group gap="sm">
          <Stack gap="xs">
            <Link
              to="/museum/$museumId/bookings/$bookingId"
              params={{
                bookingId: browseBookings?.next?.toString() ?? "",
                museumId: museumId,
              }}
              style={{ display: "inline-flex" }}
              disabled={shouldDisableNext}
            >
              <ActionIcon
                variant="light"
                size="xs"
                disabled={shouldDisableNext}
              >
                <IconChevronUp />
              </ActionIcon>
            </Link>

            <Link
              to="/museum/$museumId/bookings/$bookingId"
              params={{
                bookingId: browseBookings?.previous?.toString() ?? "",
                museumId: museumId,
              }}
              style={{ display: "inline-flex" }}
              disabled={shouldDisablePrevious}
            >
              <ActionIcon
                component="span"
                variant="light"
                size="xs"
                disabled={shouldDisablePrevious}
              >
                <IconChevronDown />
              </ActionIcon>
            </Link>
          </Stack>

          {isError ? (
            <Title order={1} c="red">
              #{bookingId} <Trans>Booking ikke funnet</Trans>
            </Title>
          ) : (
            <Title order={1}>#{bookingId}</Title>
          )}

          {/* <Group gap="xs">
              <ActionIcon variant="light">
              <IconChevronLeft />
              </ActionIcon>
              <ActionIcon variant="light">
              <IconChevronRight />
              </ActionIcon>
              </Group> */}
        </Group>

        {booking ? (
          <>
            <Group>
              <StatusSelector booking={booking.data} />

              <Text size="sm">{booking.data.location.name}</Text>

              <Stack gap={0}>
                <Text fz={10} c="dimmed">
                  {booking?.data.createdAt ? (
                    <>
                      <Trans>Opprettet:</Trans>{" "}
                      {i18n.date(booking.data.createdAt, {
                        dateStyle: "short",
                        timeStyle: "long",
                      })}
                    </>
                  ) : null}{" "}
                  {(!!booking.data.createdBy.first_name ||
                    !!booking.data.createdBy.last_name) && (
                    <Trans>
                      av {booking.data.createdBy.first_name}{" "}
                      {booking.data.createdBy.last_name}
                    </Trans>
                  )}
                </Text>
                <Text fz={10} c="dimmed">
                  {booking.data.updatedAt ? (
                    <>
                      <Trans>Sist endret:</Trans>{" "}
                      {i18n.date(booking.data.updatedAt, {
                        dateStyle: "short",
                        timeStyle: "long",
                      })}
                    </>
                  ) : null}{" "}
                  {(!!booking.data.updatedBy.first_name ||
                    !!booking.data.updatedBy.last_name) && (
                    <Trans>
                      av {booking.data.updatedBy.first_name}{" "}
                      {booking.data.updatedBy.last_name}
                    </Trans>
                  )}
                </Text>
              </Stack>
              <Can I="create" a="Booking">
                <DuplicateBooking booking={booking.data} />
              </Can>
            </Group>

            <Group gap="sm" justify="end" ml="auto">
              {/* <Button leftSection={<IconX />} variant="outline" color="red">
                <Trans>Kanseller</Trans>
                </Button> */}
              <PDFSummaryModal booking={booking.data} type="internal" />
              <PDFSummaryModal booking={booking.data} type="external" />
              <Can
                I="update"
                this={getSubject("Booking", booking.data)}
                field={
                  "sentToPowerOfficeDate" satisfies GetFieldsBySubjectKind<"Booking">
                }
              >
                <SendBooking booking={booking.data} />
              </Can>
            </Group>
          </>
        ) : (
          <Skeleton />
        )}
      </Group>

      {booking?.data.status === "cancelled" && (
        <Box p="xs" bg="red">
          <Text c="white" ta="center" fw="bolder" tt="uppercase">
            {i18n.t(bookingStatusLabels[booking.data.status])}
          </Text>
        </Box>
      )}

      {booking?.data.sentToPowerOfficeDate &&
        dayjs(booking.data.sentToPowerOfficeDate).isValid() && (
          <Box p="xs" bg="green">
            <Text c="white" ta="center" fw="bolder" tt="uppercase">
              <Trans>Sendt til PowerOffice</Trans>
            </Text>
          </Box>
        )}

      <Box pos="relative" flex={1}>
        {booking ? (
          <>
            <Grid>
              <Grid.Col span={{ base: 6, lg: 4 }}>
                <VisitInformation booking={booking.data} />
              </Grid.Col>

              <Grid.Col span={{ base: 6, lg: 4 }}>
                <GroupInformation booking={booking.data} />
              </Grid.Col>

              <Grid.Col span={{ base: 12, lg: 4 }}>
                <AdditionalInformation booking={booking.data} />
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6, xl: 8 }}>
                <Program booking={booking.data} />
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6, xl: 4 }}>
                <Products booking={booking.data} />
              </Grid.Col>

              <Grid.Col span={12}>
                <TodaysBookings booking={booking.data} museumId={museumId} />
              </Grid.Col>
            </Grid>
          </>
        ) : (
          <LoadingOverlay visible={isLoading} />
        )}
      </Box>
    </Stack>
  );
}
